/**
 * Coach Lens Dashboard
 *
 * Agent self-improvement dashboard with consent gate,
 * profile summary, growth tracking, and session insights.
 */

import { useState } from 'react'
import { motion } from 'framer-motion'
import { useAuthStore } from '@personic/shared'
import type { CoachInsight } from '@personic/shared'
import {
  useAgentProfile,
  useAgentGrowthReport,
  useCoachLensRender,
} from '@/hooks/usePragmatic'
import ConsentCard from '@/components/engage/ConsentCard'
import GrowthChart from '@/components/engage/GrowthChart'

export default function CoachLens() {
  const { user } = useAuthStore()
  const agentId = user?.id || ''

  const { data: profile, isLoading: profileLoading, refetch: refetchProfile } = useAgentProfile(agentId)
  const { data: growth, isLoading: growthLoading } = useAgentGrowthReport(agentId)

  if (profileLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex flex-col items-center">
          <div className="w-12 h-12 border-4 border-engage-primary/20 border-t-engage-primary rounded-full animate-spin" />
          <p className="mt-4 text-gray-500">Loading Coach Lens...</p>
        </motion.div>
      </div>
    )
  }

  const isConsented = profile?.consent_status === 'granted'

  return (
    <div className="space-y-8">
      {/* Header */}
      <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }}>
        <h1 className="text-2xl font-bold text-gray-900">Coach Lens</h1>
        <p className="text-gray-600 mt-1">
          Personalized coaching insights for communication improvement
        </p>
      </motion.div>

      {/* Consent Gate */}
      <ConsentCard
        agentId={agentId}
        status={profile?.consent_status || 'not_requested'}
        onStatusChange={() => refetchProfile()}
      />

      {/* Consented Content */}
      {isConsented && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="space-y-8"
        >
          {/* Agent Profile Summary */}
          <div className="card p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Your Profile</h2>

            <div className="grid md:grid-cols-3 gap-6">
              {/* Baseline Progress */}
              <div>
                <p className="text-sm text-gray-500 mb-2">Baseline Establishment</p>
                <div className="flex items-center gap-3">
                  <div className="flex-1 bg-gray-100 rounded-full h-3">
                    <motion.div
                      initial={{ width: 0 }}
                      animate={{ width: `${(profile?.establishment_progress || 0) * 100}%` }}
                      transition={{ duration: 0.5 }}
                      className="h-3 rounded-full bg-engage-primary"
                    />
                  </div>
                  <span className="text-sm font-mono text-gray-700">
                    {Math.round((profile?.establishment_progress || 0) * 100)}%
                  </span>
                </div>
                <p className="text-xs text-gray-400 mt-1">
                  {profile?.observation_count || 0} observation{(profile?.observation_count || 0) !== 1 ? 's' : ''} collected
                </p>
              </div>

              {/* Status Badge */}
              <div>
                <p className="text-sm text-gray-500 mb-2">Baseline Status</p>
                <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                  profile?.baseline_status === 'established'
                    ? 'bg-green-100 text-green-700'
                    : profile?.baseline_status === 'establishing'
                    ? 'bg-amber-100 text-amber-700'
                    : 'bg-gray-100 text-gray-600'
                }`}>
                  {profile?.baseline_status?.replace(/_/g, ' ') || 'Unknown'}
                </span>
              </div>

              {/* Stream Role */}
              <div>
                <p className="text-sm text-gray-500 mb-2">Stream Role</p>
                <span className="text-sm font-medium text-gray-900">
                  {profile?.stream_role === 'invoker' ? '🪞 Invoker' : '🎯 ' + (profile?.stream_role || 'Unknown')}
                </span>
              </div>
            </div>

            {/* Typical Patterns */}
            {profile?.typical_patterns && (
              <div className="mt-6 pt-4 border-t border-gray-100">
                <h3 className="text-sm font-medium text-gray-700 mb-3">Typical Patterns</h3>
                <div className="grid md:grid-cols-3 gap-4">
                  {profile.typical_patterns.certainty && (
                    <div className="p-3 bg-blue-50 rounded-lg">
                      <p className="text-xs text-blue-600 mb-1">Certainty</p>
                      <p className="text-lg font-semibold text-blue-800">
                        {profile.typical_patterns.certainty.band}
                      </p>
                      <p className="text-xs text-blue-500">
                        mean: {profile.typical_patterns.certainty.mean.toFixed(2)}
                      </p>
                    </div>
                  )}
                  {profile.typical_patterns.hedging && (
                    <div className="p-3 bg-amber-50 rounded-lg">
                      <p className="text-xs text-amber-600 mb-1">Hedging</p>
                      <p className="text-lg font-semibold text-amber-800">
                        {(profile.typical_patterns.hedging.frequency * 100).toFixed(0)}%
                      </p>
                      <p className="text-xs text-amber-500">frequency</p>
                    </div>
                  )}
                  {profile.typical_patterns.commitment && (
                    <div className="p-3 bg-emerald-50 rounded-lg">
                      <p className="text-xs text-emerald-600 mb-1">Commitment</p>
                      <p className="text-lg font-semibold text-emerald-800">
                        {profile.typical_patterns.commitment.band}
                      </p>
                      <p className="text-xs text-emerald-500">
                        mean: {profile.typical_patterns.commitment.mean.toFixed(2)}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Growth Tracking */}
          <div className="card p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Growth Tracking</h2>
            {growthLoading ? (
              <div className="py-8 text-center text-gray-500">Loading growth data...</div>
            ) : growth ? (
              <GrowthChart
                growthAreas={growth.growth_areas}
                coachingThemes={growth.coaching_themes}
                overallTrend={growth.overall_trend}
              />
            ) : (
              <div className="py-8 text-center">
                <p className="text-gray-500">No growth data available yet</p>
                <p className="text-xs text-gray-400 mt-1">
                  Complete more sessions to see growth trends
                </p>
              </div>
            )}
            {growth && (
              <p className="text-xs text-gray-400 mt-4">
                Based on {growth.sessions_analyzed} analyzed session{growth.sessions_analyzed !== 1 ? 's' : ''}
              </p>
            )}
          </div>

          {/* Session Insights */}
          <SessionInsightsSection agentId={agentId} profileId={agentId} />
        </motion.div>
      )}
    </div>
  )
}

// =============================================================================
// SESSION INSIGHTS SUB-COMPONENT
// =============================================================================

function SessionInsightsSection({
  agentId,
  profileId,
}: {
  agentId: string
  profileId: string
}) {
  const [sessionId, setSessionId] = useState('')
  const [insights, setInsights] = useState<CoachInsight[]>([])
  const coachRender = useCoachLensRender(profileId)

  const handleRender = async () => {
    if (!sessionId.trim()) return
    try {
      const result = await coachRender.mutateAsync({
        session_id: sessionId,
        agent_id: agentId,
        include_cross_stream: true,
      })
      setInsights(result.insights)
    } catch {
      // Error handled by React Query
    }
  }

  return (
    <div className="card p-6">
      <h2 className="text-lg font-semibold text-gray-900 mb-4">Session Insights</h2>

      <div className="flex items-center gap-3 mb-6">
        <input
          type="text"
          value={sessionId}
          onChange={(e) => setSessionId(e.target.value)}
          placeholder="Enter session ID..."
          className="flex-1 px-4 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-engage-primary/20 focus:border-engage-primary"
        />
        <button
          onClick={handleRender}
          disabled={!sessionId.trim() || coachRender.isPending}
          className="px-4 py-2 bg-engage-primary text-white rounded-lg text-sm font-medium hover:bg-engage-primary/90 transition-colors disabled:opacity-50"
        >
          {coachRender.isPending ? 'Loading...' : 'Get Insights'}
        </button>
      </div>

      {coachRender.isError && (
        <div className="p-3 bg-red-50 text-red-700 text-sm rounded-lg mb-4">
          Failed to load insights. Check the session ID and try again.
        </div>
      )}

      {/* Summary */}
      {coachRender.data?.summary && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="p-4 bg-engage-primary/5 rounded-lg mb-6"
        >
          <h3 className="font-semibold text-gray-900 mb-2">
            {coachRender.data.summary.headline}
          </h3>
          <ul className="space-y-1">
            {coachRender.data.summary.key_observations.map((obs, i) => (
              <li key={i} className="text-sm text-gray-600 flex items-start gap-2">
                <span className="text-engage-primary mt-0.5">-</span>
                <span>{obs}</span>
              </li>
            ))}
          </ul>
          {coachRender.data.summary.recommended_focus && (
            <p className="text-sm text-engage-primary font-medium mt-3">
              Focus: {coachRender.data.summary.recommended_focus}
            </p>
          )}
        </motion.div>
      )}

      {/* Insight Cards */}
      {insights.length > 0 ? (
        <div className="space-y-3">
          {insights.map((insight, index) => (
            <InsightCard key={index} insight={insight} index={index} />
          ))}
        </div>
      ) : (
        !coachRender.isPending && (
          <div className="text-center py-8">
            <div className="text-4xl mb-3 opacity-50">🎓</div>
            <p className="text-gray-500 text-sm">
              Enter a session ID above to view coaching insights
            </p>
          </div>
        )
      )}
    </div>
  )
}

function InsightCard({ insight, index }: { insight: CoachInsight; index: number }) {
  const severityConfig = {
    info: { bg: 'bg-blue-50', border: 'border-blue-200', text: 'text-blue-700', icon: 'ℹ️' },
    suggestion: { bg: 'bg-green-50', border: 'border-green-200', text: 'text-green-700', icon: '💡' },
    attention: { bg: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-700', icon: '⚠️' },
    critical: { bg: 'bg-red-50', border: 'border-red-200', text: 'text-red-700', icon: '🔴' },
  }
  const config = severityConfig[insight.severity] || severityConfig.info

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.05 }}
      className={`p-4 rounded-lg border ${config.bg} ${config.border}`}
    >
      <div className="flex items-start gap-3">
        <span className="text-lg">{config.icon}</span>
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            <h4 className={`text-sm font-semibold ${config.text}`}>
              {insight.title}
            </h4>
            <span className={`text-xs px-2 py-0.5 rounded-full ${config.bg} ${config.text}`}>
              {insight.type}
            </span>
            {insight.actionable && (
              <span className="text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-700">
                Actionable
              </span>
            )}
          </div>
          <p className="text-sm text-gray-700">{insight.description}</p>
          {insight.evidence.length > 0 && (
            <div className="mt-2 space-y-1">
              {insight.evidence.map((e, i) => (
                <p key={i} className="text-xs text-gray-500 italic">
                  "{e}"
                </p>
              ))}
            </div>
          )}
        </div>
      </div>
    </motion.div>
  )
}
